import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import GoBackButton from '@/common/components/GoBackButton'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import TextField from '@/common/components/TextField'
import { useGoBack } from '@/common/hooks/useGoBack'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useEstablishmentInfrastructure } from '@/features/maintenance/establishmentInfrastructures/hooks/useEstablishmentInfrastructure'
import { useUpdateEstablishmentInfrastructure } from '@/features/maintenance/establishmentInfrastructures/hooks/useUpdateEstablishmentInfrastructure'
import {
  establishmentInfrastructureCreateSchema,
  EstablishmentInfrastructureValidationSchema,
} from '@/features/maintenance/establishmentInfrastructures/schemas/create.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronsRightIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'

const Update = () => {
  const { id } = useParams()
  const goBack = useGoBack()
  const queryClient = useQueryClient()

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()

  const userHasPermissionToUpdateEstablishmentInfrastructure =
    isUserPermissionsPending || hasUserPermission('Admin::Maintenance::EstablishmentInfrastructures::Update')

  const { data: establishmentInfrastructure, isPending: establishmentInfrastructureIsPending } =
    useEstablishmentInfrastructure(id || '')

  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<EstablishmentInfrastructureValidationSchema>({
    resolver: zodResolver(establishmentInfrastructureCreateSchema),
    defaultValues: {
      display_name: establishmentInfrastructure?.display_name,
    },
  })

  const { mutate: updateEstablishmentInfrastructure, isPending: updateEstablishmentInfrastructureIsPending } =
    useUpdateEstablishmentInfrastructure({
      id: id || '',
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['establishmentInfrastructure', id] })
        goBack({ fallback: `/establishment-infrastructures/${id}` })
      },
      onError: FormTransformers.errorsTransformer({ setError, setMessages }),
    })

  const onSubmit: SubmitHandler<EstablishmentInfrastructureValidationSchema> = (data) =>
    updateEstablishmentInfrastructure(data)

  useEffect(() => {
    if (establishmentInfrastructureIsPending) return

    reset({
      display_name: establishmentInfrastructure?.display_name,
    })
  }, [establishmentInfrastructureIsPending])

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-128px)]"
      statusCode={userHasPermissionToUpdateEstablishmentInfrastructure ? 200 : 403}
    >
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="false" className="flex flex-col gap-y-6">
        <div className="flex justify-between items-center">
          <div className="flex gap-2 items-center">
            <GoBackButton fallback={`/establishment-infrastructures/${id}`} />

            <p className="text-white-400 font-normal">Infraestrutura da Rede Credenciada</p>

            <ChevronsRightIcon className="text-white-400" />

            <h2>{establishmentInfrastructure?.display_name}</h2>
          </div>

          <div className="flex gap-4">
            <Button
              variant="outline-error"
              onClick={() => goBack({ fallback: `/establishment-infrastructures/${id}` })}
            >
              Cancelar
            </Button>

            <Button
              type="submit"
              name="update_establishment_infrastructure"
              className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
              disabled={updateEstablishmentInfrastructureIsPending}
              isLoading={updateEstablishmentInfrastructureIsPending}
            >
              Salvar
            </Button>
          </div>
        </div>

        {establishmentInfrastructureIsPending ? (
          <Box>Carregando...</Box>
        ) : (
          <Box className="max-h-[calc(100vh-128px)] flex flex-col gap-4 overflow-y-auto">
            <p className="text-lg font-bold">Infraestrutura da Rede Credenciada</p>

            <TextField
              label="Nome da infraestrutura"
              markAsRequired
              {...register('display_name')}
              errorMessage={errors?.display_name?.message}
              disabled={updateEstablishmentInfrastructureIsPending}
            />
          </Box>
        )}

        {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
      </form>
    </ErrorMessageWrapper>
  )
}

export default Update
